import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useChatRoomRailStore } from '../../store/chatRoomRailStore';

/**
 * 우측 고정 메뉴바의 **채팅방 프로필 카드** — 메모 버튼 아래(레일 children 맨 앞).
 *
 * 방 아이콘 + 현재 인원 동그라미 배지. 누르면 상세보기 패널이 열리고 닫힌다.
 *
 * ⚠ 대화방명 툴팁은 **body 로 portal** 한다 — 레일이 `overflow:hidden` 이라 안에 두면 잘린다
 *   (같은 이유로 ProfilePopover 도 portal 이다). 위치는 카드 왼쪽에 붙여 계산한다.
 *
 * 채팅 화면이 아닐 때는 스토어가 비어 있어 아무것도 그리지 않는다 — 전역 레일이 채팅을
 * 직접 알지 않도록 [[chatRoomRailStore]] 를 사이에 둔다.
 */
export function ChatRoomRailCard() {
  const info = useChatRoomRailStore((s) => s.info);
  const panelOpen = useChatRoomRailStore((s) => s.panelOpen);
  const onToggle = useChatRoomRailStore((s) => s.onToggle);

  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [tip, setTip] = useState<{ top: number; right: number } | null>(null);

  const show = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // right = 화면 오른쪽 끝에서 카드 왼쪽까지 — 레일 바깥(왼쪽)에 붙인다.
    setTip({ top: r.top + r.height / 2, right: window.innerWidth - r.left + 8 });
  }, []);
  const hide = useCallback(() => setTip(null), []);

  if (!info) return null;

  const mark = info.name.trim().charAt(0) || '#';
  const count = info.memberCount > 999 ? '999+' : String(info.memberCount);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="app-rightrail__roomcard"
        aria-expanded={panelOpen}
        aria-label={`${info.name} · 참여자 ${info.memberCount}명`}
        onClick={() => onToggle?.()}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <span className="app-rightrail__roomico-wrap">
          <span className="app-rightrail__roomico" aria-hidden>{mark}</span>
          <span className="app-rightrail__roombadge" aria-hidden>{count}</span>
        </span>
      </button>
      {tip && createPortal(
        <span
          className="app-rightrail__roomtip"
          role="tooltip"
          style={{ top: tip.top, right: tip.right }}
        >
          {info.name}
        </span>,
        document.body,
      )}
    </>
  );
}
